import React, { useState } from 'react';
import { theme } from '../theme';
import Icons from './Icons';
import { Button, Spinner } from './ui';
import { apiPost } from '../api';
import SddChipsAndWarnings from './SddChipsAndWarnings';
import { useConfig } from '../context/ConfigContext';
import { isWmsConnection as isWms, credentialsFor } from '../utils/connectionRouting';

// ---------------------------------------------------------------------------
// Generate SQL — sidebar panel with AI generation + saved prompts list
// ---------------------------------------------------------------------------
export default function GenerateSQL({ schema, onGenerate, savedItems, onSave, onDelete, onSelect, currentSql, currentAiPrompt, onRefine }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState(null);
  const [sddContext,  setSddContext]  = useState([]); // schemas used by last generation
  const [sddWarnings, setSddWarnings] = useState([]); // column existence warnings
  const [assumptions, setAssumptions] = useState([]); // how the model read the prompt

  // The context chips name Fusion schemas from the Data Dictionary, so they are
  // meaningless — and misleading — on a WMS connection.
  const { config } = useConfig();
  const isWmsConnection = isWms({ type: config.connectionType });

  // On a WMS connection the server needs the pod credentials to call describe/
  // and put real field lists in the prompt. Fusion generation needs none of
  // this — its schemas come from the local Data Dictionary.
  const backendContext = isWmsConnection ? credentialsFor({ ...config, type: config.connectionType }) : {};

  // After generation: show save form
  const [pendingSave, setPendingSave] = useState(null); // { prompt, sql, title }

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setPendingSave(null);
    setSddContext([]);
    setSddWarnings([]);
    setAssumptions([]);
    try {
      const result = await apiPost('/llm/generate-sql', {
        prompt: prompt.trim(),
        schema: schema || [],
        ...backendContext,
      });
      const title = result.title || prompt.trim().slice(0, 55);
      onGenerate(result.sql, title, prompt.trim());
      setSddContext(result.sddContext || []);
      setSddWarnings(result.sddWarnings || []);
      setAssumptions(result.assumptions || []);
      setPendingSave({
        prompt: prompt.trim(),
        sql: result.sql,
        title,
      });
      setPrompt('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refine edits the query already in the editor instead of starting over.
  // Available whenever the active tab holds an AI-generated query.
  const canRefine = Boolean(onRefine && currentSql && currentSql.trim() && currentAiPrompt);

  const handleRefine = async () => {
    if (!prompt.trim() || !canRefine) return;
    setRefining(true);
    setError(null);
    setPendingSave(null);
    setSddContext([]);
    setSddWarnings([]);
    setAssumptions([]);
    try {
      const result = await apiPost('/llm/refine-sql', {
        prompt:      currentAiPrompt,
        sql:         currentSql,
        instruction: prompt.trim(),
        schema:      schema || [],
      });
      onRefine(result.sql, result.title);
      setSddContext(result.sddContext || []);
      setSddWarnings(result.sddWarnings || []);
      setAssumptions(result.assumptions || []);
      setPrompt('');
    } catch (err) {
      setError(err.message);
    } finally {
      setRefining(false);
    }
  };

  const handleSave = () => {
    if (!pendingSave) return;
    onSave(pendingSave.title.trim() || pendingSave.prompt, pendingSave.prompt, pendingSave.sql);
    setPendingSave(null);
    setSddContext([]);
    setSddWarnings([]);
    setAssumptions([]);
  };

  // Load a saved item back into the prompt textarea for iteration
  const handleSelect = (item) => {
    setPrompt(item.prompt);
    setPendingSave(null);
    setError(null);
    onSelect(item); // also loads SQL into editor
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>

      {/* Prompt textarea */}
      <div style={{ position: 'relative' }}>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              e.preventDefault();
              handleGenerate();
            }
          }}
          placeholder={canRefine
            ? 'Describe a new query, or a change to the current one — e.g. also include the department name'
            : 'e.g. show me all active employees hired this year'}
          rows={4}
          style={{
            width: '100%',
            padding: prompt ? '8px 26px 8px 10px' : '8px 10px',
            background: theme.codeBg,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            color: theme.text,
            fontSize: '12px',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit',
            lineHeight: 1.5,
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
          onBlur={(e) => { e.target.style.borderColor = theme.border; }}
        />
        {prompt && (
          <button
            onClick={() => setPrompt('')}
            title="Clear"
            style={{
              position: 'absolute', top: '7px', right: '7px',
              background: 'none', border: 'none', padding: '2px',
              cursor: 'pointer', color: theme.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '3px', lineHeight: 0,
              transition: 'color 0.12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
          >
            <Icons.X style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px' }}>
        <Button
          onClick={handleGenerate}
          disabled={loading || refining || !prompt.trim()}
          icon={loading ? <Spinner /> : <Icons.Zap />}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {loading ? 'Generating...' : 'Generate SQL'}
        </Button>
        {canRefine && (
          <button
            onClick={handleRefine}
            disabled={loading || refining || !prompt.trim()}
            title="Apply this as a change to the query in the editor, instead of writing a new one"
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              background: 'none', border: `1px solid ${theme.border}`, borderRadius: '6px',
              color: prompt.trim() && !loading && !refining ? theme.text : theme.textMuted,
              fontSize: '12px', fontWeight: 600, padding: '0 10px',
              cursor: prompt.trim() && !loading && !refining ? 'pointer' : 'not-allowed',
              opacity: prompt.trim() && !loading && !refining ? 1 : 0.55,
            }}
          >
            {refining ? <Spinner /> : null}
            {refining ? 'Refining...' : 'Refine current'}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ fontSize: '12px', color: theme.danger, padding: '6px 8px', background: `${theme.danger}12`, borderRadius: '5px' }}>
          {error}
        </div>
      )}

      {/* SDD context chips + column warnings — Fusion only; see isWmsConnection */}
      {!isWmsConnection && (
        <SddChipsAndWarnings sddContext={sddContext} sddWarnings={sddWarnings} assumptions={assumptions} />
      )}

      {/* WMS: a leftover warning means the query will not compile, so say so
          here rather than letting the user discover it by pressing Run. */}
      {isWmsConnection && sddWarnings.length > 0 && (
        <div style={{
          fontSize: '12px', color: theme.warning,
          background: `${theme.warning}14`, border: `1px solid ${theme.warning}55`,
          borderRadius: '6px', padding: '8px 10px', display: 'flex',
          flexDirection: 'column', gap: '4px',
        }}>
          <strong style={{ fontWeight: 600 }}>This query will not run on WMS</strong>
          {sddWarnings.map((w, i) => (
            <span key={i}>{w.message || w.suggestion || String(w)}</span>
          ))}
        </div>
      )}

      {/* WMS assumptions matter more than on Fusion — they are where the model
          reports that a join or aggregate had to be dropped. */}
      {isWmsConnection && assumptions.length > 0 && (
        <div style={{ fontSize: '12px', color: theme.textMuted, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {assumptions.map((a, i) => <span key={i}>• {a}</span>)}
        </div>
      )}

      {/* Save form — appears after successful generation */}
      {pendingSave && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '6px',
          padding: '10px', borderRadius: '6px',
          background: `${theme.success}0d`,
          border: `1px solid ${theme.success}33`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.success, fontWeight: 600 }}>
            <Icons.Check style={{ width: 13, height: 13, flexShrink: 0 }} />
            SQL generated — save it?
          </div>
          <input
            value={pendingSave.title}
            onChange={(e) => setPendingSave(p => ({ ...p, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setPendingSave(null); }}
            placeholder="Title…"
            autoFocus
            style={{
              width: '100%', padding: '6px 8px',
              background: theme.codeBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '5px', color: theme.text,
              fontSize: '12px', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
            onBlur={(e) => { e.target.style.borderColor = theme.border; }}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleSave}
              disabled={!pendingSave.title.trim()}
              style={{
                flex: 1, background: theme.accent, color: '#fff', border: 'none',
                borderRadius: '5px', padding: '5px 0', fontSize: '12px',
                fontWeight: 600, cursor: pendingSave.title.trim() ? 'pointer' : 'not-allowed',
                opacity: pendingSave.title.trim() ? 1 : 0.5,
              }}
            >
              Save
            </button>
            <button
              onClick={() => { setPendingSave(null); setSddContext([]); setSddWarnings([]); setAssumptions([]); }}
              style={{
                background: 'none', border: `1px solid ${theme.border}`,
                borderRadius: '5px', padding: '5px 10px', fontSize: '12px',
                color: theme.textMuted, cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: '11px', color: theme.textMuted, paddingLeft: '2px' }}>
        Ctrl+Enter to generate{canRefine ? ' · Refine edits the query in the editor' : ''}
      </div>

      {/* Saved list */}
      {savedItems.length > 0 && (
        <>
          <div style={{ height: '1px', background: theme.border, margin: '4px 0' }} />
          <div style={{ fontSize: '11px', fontWeight: 600, color: theme.textMuted, paddingLeft: '2px', marginBottom: '2px' }}>
            SAVED ({savedItems.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
            {savedItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleSelect(item)}
                title="Click to load prompt and iterate"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '6px',
                  padding: '7px 8px', borderRadius: '6px',
                  cursor: 'pointer', position: 'relative',
                  border: `1px solid transparent`,
                  transition: 'background 0.12s, border-color 0.12s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.surfaceHover;
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.querySelector('.del-btn').style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.querySelector('.del-btn').style.opacity = '0';
                }}
              >
                <Icons.Zap style={{ width: 12, height: 12, color: theme.accent, flexShrink: 0, marginTop: '3px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Title */}
                  <div style={{
                    fontSize: '12px', fontWeight: 600, color: theme.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.title || item.prompt}
                  </div>
                  {/* Prompt preview */}
                  <div style={{
                    fontSize: '11px', color: theme.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginTop: '1px',
                  }}>
                    {item.prompt}
                  </div>
                  {/* SQL preview */}
                  <div style={{
                    fontSize: '10px', color: theme.textMuted, fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginTop: '2px', opacity: 0.7,
                  }}>
                    {item.sql.replace(/\s+/g, ' ').slice(0, 60)}{item.sql.length > 60 ? '…' : ''}
                  </div>
                </div>
                <button
                  className="del-btn"
                  onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                  style={{
                    background: 'none', border: 'none', padding: '2px',
                    color: theme.textMuted, cursor: 'pointer', opacity: 0,
                    transition: 'opacity 0.15s', flexShrink: 0,
                  }}
                  title="Remove"
                >
                  <Icons.X style={{ width: 11, height: 11 }} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
